import { isTable } from '../../../common/elements/utils/elementTypeUtils';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { finishEditingCurrentElement } from '../../element/selection/selectionActions';
import { popupsOpenSelector } from '../../components/popupPanel/popupOpenSelector';
import { POPUPS_BY_SECTION, PopupSections } from '../../components/popupPanel/popupConstants';
import useUpdatedRef from '../react/useUpdatedRef';
import { getIsAnySheetOpen } from '../../mobile/structural/sheet/sheetSelectors';
import { getCurrentlyEditingElement } from '../../element/selection/currentlyEditingSelector';
import { getIsModalOpen } from '../../components/modal/activeModalSelector';
import { Capacitor } from '@capacitor/core';
import { CAPACITOR_MN_KEYBOARD_PLUGIN } from '../../../capacitor_plugins/pluginConstants';
import { KeyboardPlugin } from '../../../capacitor_plugins/keyboard';
import { getPlatformDetailsSelector } from '../../platform/platformSelector';
import { isPlatformIos } from '../../platform/utils/platformDetailsUtils';

export default () => {
    const dispatch = useDispatch();
    const dispatchFinishEditing = () => dispatch(finishEditingCurrentElement());

    const toolbarPopupOpen = useSelector(popupsOpenSelector(POPUPS_BY_SECTION[PopupSections.TOOLBAR]));
    const toolbarPopupOpenRef = useUpdatedRef(toolbarPopupOpen);

    const isAnySheetOpen = useSelector(getIsAnySheetOpen);
    const isAnySheetOpenRef = useUpdatedRef(isAnySheetOpen);

    const isAnyModalOpen = useSelector(getIsModalOpen);
    const isAnyModalOpenRef = useUpdatedRef(isAnyModalOpen);

    const currentlyEditingElement = useSelector(getCurrentlyEditingElement);
    const currentlyEditingElementRef = useUpdatedRef(currentlyEditingElement);

    const platformDetails = useSelector(getPlatformDetailsSelector);

    /**
     * Determine whether to dispatchFinishEditing when the keyboard hides.
     * e.g. Should dispatchFinishEditing when user tap the done button on the iOS keyboard,
     *      so that the toolbar change from editing mode to selection mode
     */
    const shouldDispatchFinishEditingOnKeyboardHide = async () => {
        // When keyboard hides and a sheet is open, we don't want to finish editing as the user is likely
        // still interacting with the sheet
        if (isAnySheetOpenRef.current) return false;

        // When keyboard hides and a modal is open, we don't want to finish editing as the user is likely
        // still interacting with the modal (e.g. document modal)
        if (isAnyModalOpenRef.current) return false;

        // When keyboard hides and the currently editing element is a table, we don't want to finish editing as
        // the keyboard hiding might be due to interactions with the table toolbar popup
        // TODO: When we enable table editing on mobile, we would need to update this check
        if (isTable(currentlyEditingElementRef.current) && toolbarPopupOpenRef.current) return false;

        // If a hardware keyboard is connected, we don't want to finish editing on keyboard hide.
        // This is because the "keyboardWillHide" event is triggered even when the virtual keyboard was never visible due to a hardware keyboard being connected.
        // In such cases, we want to allow the user to continue editing without automatically finishing.
        // For reference, the task for this bug is WEB-13222.
        // This check is only relevant for iOS.
        if (Capacitor.isPluginAvailable(CAPACITOR_MN_KEYBOARD_PLUGIN) && isPlatformIos(platformDetails)) {
            try {
                const result = await KeyboardPlugin.isHardwareKeyboardConnected();
                if (result.connected) {
                    return false;
                }
            } catch (error) {
                // If the plugin call fails, continue with default behavior
                console.warn('Failed to check hardware keyboard status:', error);
            }
        }

        return true;
    };

    useEffect(() => {
        const handleKeyboardWillHide = async () => {
            const shouldFinishEditing = await shouldDispatchFinishEditingOnKeyboardHide();
            if (!shouldFinishEditing) return;

            dispatchFinishEditing();
        };

        window.addEventListener('keyboardWillHide', handleKeyboardWillHide);

        return () => {
            window.removeEventListener('keyboardWillHide', handleKeyboardWillHide);
        };
    });
};
